import { Box, Typography, Container, Stack, Grid, Card, CardContent, Divider, useTheme, CircularProgress, Alert, Button, Collapse, Autocomplete, TextField, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Business as BusinessIcon, Build as TechnicianIcon, Error as ErrorIcon, AttachMoney as MoneyIcon, ShowChart as ChartIcon, Schedule as ScheduleIcon, CalendarToday as CalendarIcon, ExpandMore as ExpandMoreIcon, FilterList as FilterListIcon, Person as PersonIcon, Receipt as ReceiptIcon } from "@mui/icons-material";
import { useSelector } from 'react-redux';
import { getClients } from '../../../store/slices/clientsSlice';
import { getAdmins, getDispatchers } from '../../../store/slices/usersSlice';
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getUser } from "../../../store/slices/userSlice";
import { canViewDashboard, isManagement, primaryRole, primaryRoleLabel, ROLES } from "../../../utils/permissions";
import { GET } from '../../../configs/api';
import { api } from '../../../utils/constants';
import { PRIORITY_OPTIONS } from '../../../utils/jobPriorities';
import DateRangePickerDropdown from '../../../components/DateRangePickerDropdown';
import DashboardSavedFiltersBar from './DashboardSavedFiltersBar';
import {
    captureFilterSnapshot,
    clearDefaultSearch,
    deleteSearch,
    EMPTY_FILTER_SNAPSHOT,
    findSearchById,
    getDefaultSearch,
    loadDashboardSavedFilters,
    pruneSavedSearchesAgainstClients,
    renameSearch,
    sanitizeFilterSnapshot,
    setDefaultSearch,
    snapshotsMatch,
    snapshotToFilterState,
    upsertSearch,
} from './DashboardSavedFiltersHelper';

export default function Dashboard() {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    
    const user = useSelector(getUser);
    const companyId = user?.company_id ?? null;
    const isPrimaryAdmin = primaryRole(user) === ROLES.ADMIN;
    const clientsData = useSelector(getClients);
    // Ready only after a successful load — error payloads set loading=false with clients=[].
    const clientsReady = clientsData?.loading === false && !clientsData?.error;
    const admins = useSelector(getAdmins);
    const dispatchers = useSelector(getDispatchers);

    // State for main metrics
    const [metrics, setMetrics] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(true);
    const [metricsError, setMetricsError] = useState(null);

    // State for filtered metrics
    const [filteredMetrics, setFilteredMetrics] = useState(null);
    const [filteredMetricsLoading, setFilteredMetricsLoading] = useState(false);
    const [filteredMetricsError, setFilteredMetricsError] = useState(null);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [savedSearchesStorage, setSavedSearchesStorage] = useState(() => loadDashboardSavedFilters(companyId));
    const [activeSavedSearchId, setActiveSavedSearchId] = useState(null);
    const suppressCustomDetectionRef = useRef(false);
    const defaultFiltersLoadedRef = useRef(false);
    const loadedCompanyIdRef = useRef(companyId);
    
    // Filter state
    const [filters, setFilters] = useState(() => snapshotToFilterState(EMPTY_FILTER_SNAPSHOT));

    // Check if user has permission to view dashboard
    const isUserReady = Array.isArray(user?.roles) && user.roles.length > 0;
    const hasPermission = canViewDashboard(user);
    const canSeeAdminFilter = isManagement(user);

    // Fetch dashboard metrics on mount
    useEffect(() => {
        if (hasPermission) {
            fetchMetrics();
        }
    }, [hasPermission]);

    const fetchMetrics = async () => {
        try {
            setMetricsLoading(true);
            setMetricsError(null);
            const { data, status } = await GET(api.getDashboardMetrics);
            if (status === 200) {
                setMetrics(data);
            } else {
                setMetricsError('Failed to load dashboard metrics');
            }
        } catch (error) {
            console.error('Error fetching dashboard metrics:', error);
            setMetricsError(error.response?.data?.error || 'Failed to load dashboard metrics');
        } finally {
            setMetricsLoading(false);
        }
    };

    const sanitizeOptions = useMemo(() => {
        const options = {
            adminOptions: admins,
            dispatcherOptions: dispatchers,
        };
        // Strict client sanitize only for primary-role admins once clients have loaded successfully.
        if (isPrimaryAdmin && clientsReady) {
            options.clientOptions = clientsData.clients || [];
        }
        return options;
    }, [clientsData.clients, admins, dispatchers, isPrimaryAdmin, clientsReady]);

    const fetchFilteredMetrics = useCallback(async (filterOverride) => {
        const activeFilters = filterOverride ?? filters;

        try {
            setFilteredMetricsLoading(true);
            setFilteredMetricsError(null);
            
            const queryParams = new URLSearchParams();
            if (activeFilters.clientId) queryParams.append('clientId', activeFilters.clientId);
            if (activeFilters.dateFrom) queryParams.append('dateFrom', activeFilters.dateFrom.toISOString());
            if (activeFilters.dateTo) queryParams.append('dateTo', activeFilters.dateTo.toISOString());
            if (activeFilters.invoiceDateFrom) queryParams.append('invoiceDateFrom', activeFilters.invoiceDateFrom.toISOString());
            if (activeFilters.invoiceDateTo) queryParams.append('invoiceDateTo', activeFilters.invoiceDateTo.toISOString());
            if (activeFilters.dispatcherId) queryParams.append('dispatcherId', activeFilters.dispatcherId);
            if (activeFilters.adminId) queryParams.append('adminId', activeFilters.adminId);
            if (activeFilters.priority) queryParams.append('priority', activeFilters.priority);

            const { data, status } = await GET(`${api.getFilteredDashboardMetrics}?${queryParams.toString()}`);
            if (status === 200) {
                setFilteredMetrics(data);
                setFiltersApplied(true);
            } else {
                setFilteredMetricsError('Failed to load filtered metrics');
            }
        } catch (error) {
            console.error('Error fetching filtered metrics:', error);
            setFilteredMetricsError(error.response?.data?.error || 'Failed to load filtered metrics');
        } finally {
            setFilteredMetricsLoading(false);
        }
    }, [filters]);

    const getCurrentFilterSnapshot = useCallback(
        () => captureFilterSnapshot(filters),
        [filters],
    );

    const applyFilterSnapshot = useCallback((snapshot) => {
        const sanitized = sanitizeFilterSnapshot(snapshot, sanitizeOptions);
        suppressCustomDetectionRef.current = true;
        setFilters(snapshotToFilterState(sanitized));
    }, [sanitizeOptions]);

    const handleResetFilters = useCallback(() => {
        applyFilterSnapshot(EMPTY_FILTER_SNAPSHOT);
        setFilteredMetrics(null);
        setFiltersApplied(false);
        setActiveSavedSearchId(null);
    }, [applyFilterSnapshot]);

    const handleApplySavedSearch = useCallback((searchId) => {
        const search = findSearchById(savedSearchesStorage, searchId);
        if (!search) return;
        const nextFilters = snapshotToFilterState(
            sanitizeFilterSnapshot(search.filters, sanitizeOptions),
        );
        suppressCustomDetectionRef.current = true;
        setFilters(nextFilters);
        setActiveSavedSearchId(search.id);
        fetchFilteredMetrics(nextFilters);
    }, [savedSearchesStorage, sanitizeOptions, fetchFilteredMetrics]);

    const handleSaveSearch = useCallback(({ name, setAsDefault }) => {
        if (!companyId) return;
        const { storage: nextStorage, searchId } = upsertSearch(companyId, savedSearchesStorage, {
            name,
            filters: getCurrentFilterSnapshot(),
            setAsDefault,
        });
        setSavedSearchesStorage(nextStorage);
        setActiveSavedSearchId(searchId);
    }, [companyId, savedSearchesStorage, getCurrentFilterSnapshot]);

    const handleDeleteSavedSearch = useCallback((searchId) => {
        if (!companyId) return;
        const nextStorage = deleteSearch(companyId, savedSearchesStorage, searchId);
        setSavedSearchesStorage(nextStorage);
        if (activeSavedSearchId === searchId) {
            setActiveSavedSearchId(null);
        }
    }, [companyId, savedSearchesStorage, activeSavedSearchId]);

    const handleRenameSavedSearch = useCallback((searchId, name) => {
        if (!companyId) return;
        const nextStorage = renameSearch(companyId, savedSearchesStorage, searchId, name);
        setSavedSearchesStorage(nextStorage);
    }, [companyId, savedSearchesStorage]);

    const handleSetDefaultSavedSearch = useCallback((searchId) => {
        if (!companyId) return;
        const nextStorage = setDefaultSearch(companyId, savedSearchesStorage, searchId);
        setSavedSearchesStorage(nextStorage);
    }, [companyId, savedSearchesStorage]);

    const handleClearDefaultSavedSearch = useCallback(() => {
        if (!companyId) return;
        const nextStorage = clearDefaultSearch(companyId, savedSearchesStorage);
        setSavedSearchesStorage(nextStorage);
    }, [companyId, savedSearchesStorage]);

    // Reload company-scoped saved filters when the active company changes.
    useEffect(() => {
        if (loadedCompanyIdRef.current === companyId) return;
        loadedCompanyIdRef.current = companyId;
        defaultFiltersLoadedRef.current = false;
        setActiveSavedSearchId(null);
        setFilteredMetrics(null);
        setFiltersApplied(false);
        setSavedSearchesStorage(loadDashboardSavedFilters(companyId));
        setFilters(snapshotToFilterState(EMPTY_FILTER_SNAPSHOT));
    }, [companyId]);

    // Primary-role admin: drop inaccessible clientId from this company's saved filters.
    useEffect(() => {
        if (!hasPermission || !isPrimaryAdmin || !clientsReady || !companyId) return;
        if (loadedCompanyIdRef.current !== companyId) return;

        const clientOptions = clientsData.clients || [];
        const pruned = pruneSavedSearchesAgainstClients(
            companyId,
            loadDashboardSavedFilters(companyId),
            clientOptions,
        );
        setSavedSearchesStorage(pruned.storage);

        const nextClientId = sanitizeFilterSnapshot(
            { clientId: filters.clientId },
            { clientOptions },
        ).clientId;
        if (String(filters.clientId || '') === String(nextClientId || '')) return;

        suppressCustomDetectionRef.current = true;
        setFilters((prev) => ({ ...prev, clientId: nextClientId }));
        // Drop stale filtered metrics that were computed for a revoked client.
        setFilteredMetrics(null);
        setFiltersApplied(false);
    }, [hasPermission, isPrimaryAdmin, clientsReady, clientsData.clients, companyId, filters.clientId]);

    useEffect(() => {
        if (!hasPermission || defaultFiltersLoadedRef.current) return;
        defaultFiltersLoadedRef.current = true;

        const storage = loadDashboardSavedFilters(companyId);
        setSavedSearchesStorage(storage);
        const defaultSearch = getDefaultSearch(storage);
        const snapshot = defaultSearch ? defaultSearch.filters : EMPTY_FILTER_SNAPSHOT;
        const nextFilters = snapshotToFilterState(
            sanitizeFilterSnapshot(snapshot, sanitizeOptions),
        );

        suppressCustomDetectionRef.current = true;
        setFilters(nextFilters);
        setActiveSavedSearchId(defaultSearch ? defaultSearch.id : null);

        if (defaultSearch) {
            fetchFilteredMetrics(nextFilters);
        }
    }, [hasPermission, companyId, sanitizeOptions, fetchFilteredMetrics]);

    useEffect(() => {
        if (suppressCustomDetectionRef.current) {
            suppressCustomDetectionRef.current = false;
            return;
        }
        if (!activeSavedSearchId) return;

        const activeSearch = findSearchById(savedSearchesStorage, activeSavedSearchId);
        if (!activeSearch) {
            setActiveSavedSearchId(null);
            return;
        }

        const sanitizedCurrent = sanitizeFilterSnapshot(getCurrentFilterSnapshot(), sanitizeOptions);
        if (!snapshotsMatch(sanitizedCurrent, activeSearch.filters)) {
            setActiveSavedSearchId(null);
        }
    }, [
        filters,
        activeSavedSearchId,
        savedSearchesStorage,
        sanitizeOptions,
        getCurrentFilterSnapshot,
    ]);

    const handleClearFilters = () => {
        handleResetFilters();
    };

    const handleApplyFilters = () => {
        fetchFilteredMetrics();
    };

    const selectedClient = useMemo(
        () => (clientsData.clients || []).find((client) => String(client.id) === String(filters.clientId)) || null,
        [clientsData.clients, filters.clientId],
    );

    const selectedAdmin = useMemo(
        () => admins.find((admin) => String(admin.id) === String(filters.adminId)) || null,
        [admins, filters.adminId],
    );

    const selectedDispatcher = useMemo(
        () => dispatchers.find((dispatcher) => String(dispatcher.id) === String(filters.dispatcherId)) || null,
        [dispatchers, filters.dispatcherId],
    );

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.clientId) count += 1;
        if (filters.priority) count += 1;
        if (filters.adminId) count += 1;
        if (filters.dispatcherId) count += 1;
        if (filters.dateFrom || filters.dateTo) count += 1;
        if (filters.invoiceDateFrom || filters.invoiceDateTo) count += 1;
        return count;
    }, [filters]);

    const handlePriorityToggle = (priorityValue) => {
        const value = String(priorityValue);
        setFilters((prev) => ({
            ...prev,
            priority: prev.priority === value ? '' : value,
        }));
    };

    const filterInputSx = useMemo(() => ({
        '& .MuiOutlinedInput-root': {
            bgcolor: 'background.paper',
            borderRadius: 1.5,
            fontSize: '0.875rem',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '& fieldset': {
                borderColor: isDarkMode ? '#334155' : '#e2e8f0',
            },
            '&:hover fieldset': {
                borderColor: isDarkMode ? '#475569' : '#cbd5e1',
            },
            '&.Mui-focused fieldset': {
                borderColor: isDarkMode ? '#94a3b8' : '#64748b',
                borderWidth: '1px',
            },
        },
        '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
        },
    }), [isDarkMode]);

    const priorityChipSx = (priorityValue) => {
        const isSelected = filters.priority === String(priorityValue);
        const palette = {
            1: { bg: isDarkMode ? '#7f1d1d' : '#fee2e2', fg: isDarkMode ? '#fca5a5' : '#991b1b', border: '#ef4444' },
            2: { bg: isDarkMode ? '#78350f' : '#fef3c7', fg: isDarkMode ? '#fbbf24' : '#92400e', border: '#f59e0b' },
            3: { bg: isDarkMode ? '#1e3a8a' : '#dbeafe', fg: isDarkMode ? '#93c5fd' : '#1e3a8a', border: '#3b82f6' },
            4: { bg: isDarkMode ? '#374151' : '#e5e7eb', fg: isDarkMode ? '#d1d5db' : '#374151', border: '#6b7280' },
        };
        const colors = palette[priorityValue] || palette[4];

        return {
            height: 32,
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: 'pointer',
            borderRadius: '8px',
            backgroundColor: isSelected ? colors.bg : 'transparent',
            color: isSelected ? colors.fg : 'text.secondary',
            borderColor: isSelected ? colors.border : (isDarkMode ? '#334155' : '#e2e8f0'),
            '&:hover': {
                backgroundColor: isSelected ? colors.bg : (isDarkMode ? '#1e293b' : '#f8fafc'),
                borderColor: colors.border,
            },
        };
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const surfaceSx = {
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        height: '100%',
    };

    const sectionTitleSx = {
        fontWeight: 600,
        fontSize: '0.8125rem',
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        mb: 1.5,
    };

    const KpiCard = ({ title, value, icon, color, subtitle }) => (
        <Box sx={{ ...surfaceSx, p: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(color, 0.1),
                flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color, fontSize: 18 } })}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                    {title}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mt: 0.25 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    const Panel = ({ title, children, sx }) => (
        <Box sx={{ ...surfaceSx, p: { xs: 1.5, sm: 2 }, ...sx }}>
            {title && <Typography sx={sectionTitleSx}>{title}</Typography>}
            {children}
        </Box>
    );

    const StatusMetric = ({ label, count, color }) => (
        <Box sx={{ textAlign: 'center', px: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color, lineHeight: 1 }}>
                {count}
            </Typography>
        </Box>
    );

    const FinancialPanel = ({ title, description, financial }) => {
        const revenue = financial?.revenue ?? 0;
        const cost = financial?.cost ?? 0;
        const profit = financial?.profit ?? 0;
        const margin = revenue > 0 ? ((profit / revenue) * 100).toFixed(1) : null;

        return (
            <Panel title={title}>
                {description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.4 }}>
                        {description}
                    </Typography>
                )}
                <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                    <Grid size={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Revenue</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#10b981' }}>
                            {formatCurrency(revenue)}
                        </Typography>
                    </Grid>
                    <Grid size={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Cost</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#ef4444' }}>
                            {formatCurrency(cost)}
                        </Typography>
                    </Grid>
                    <Grid size={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Profit</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: profit >= 0 ? '#10b981' : '#ef4444' }}>
                            {formatCurrency(profit)}
                        </Typography>
                        {margin !== null && (
                            <Typography variant="caption" color="text.secondary">{margin}%</Typography>
                        )}
                    </Grid>
                </Grid>
            </Panel>
        );
    };

    const WorkOrdersPanel = ({ workOrders }) => {
        const periods = [
            { label: 'Today', count: workOrders?.today || 0 },
            { label: 'Yesterday', count: workOrders?.yesterday || 0 },
            { label: 'This week', count: workOrders?.thisWeek || 0 },
            { label: 'Last week', count: workOrders?.lastWeek || 0 },
            { label: 'This month', count: workOrders?.thisMonth || 0 },
            { label: 'Last month', count: workOrders?.lastMonth || 0 },
        ];

        return (
            <Panel title="New work orders">
                <Grid container spacing={1}>
                    {periods.map((item) => (
                        <Grid size={{ xs: 6, sm: 4 }} key={item.label}>
                            <Box sx={{
                                p: 1.25,
                                borderRadius: 1.5,
                                bgcolor: isDarkMode ? alpha('#fff', 0.03) : alpha('#000', 0.02),
                                textAlign: 'center',
                            }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {item.label}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.25 }}>
                                    {item.count}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Panel>
        );
    };

    const kpiItems = (metricsData) => [
        { title: 'Clients', value: metricsData?.totalClients || 0, icon: <BusinessIcon />, color: '#3b82f6' },
        { title: 'Technicians', value: metricsData?.totalTechnicians || 0, icon: <TechnicianIcon />, color: '#a855f7' },
        { title: 'Unscheduled', value: metricsData?.unscheduledJobs || 0, icon: <ScheduleIcon />, color: '#f59e0b' },
        {
            title: 'Open invoices',
            value: metricsData?.openInvoicesCount || 0,
            icon: <MoneyIcon />,
            color: '#ef4444',
            subtitle: `${formatCurrency(metricsData?.totalUnpaid || 0)} unpaid`,
        },
        {
            title: 'All jobs profit',
            value: formatCurrency(metricsData?.financial?.allJobs?.profit ?? 0),
            icon: <ChartIcon />,
            color: '#10b981',
            subtitle: 'NTE − cost',
        },
    ];

    const DashboardMetricsSection = ({ metricsData, compact = false }) => (
        <Stack spacing={compact ? 2 : 2.5}>
            <Grid container spacing={1.5}>
                {kpiItems(metricsData).map((kpi) => (
                    <Grid size={{ xs: 6, sm: 4, md: compact ? 6 : 2.4 }} key={kpi.title}>
                        <KpiCard {...kpi} />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel title="Jobs">
                        <Grid container>
                            <Grid size={4}><StatusMetric label="Created" count={metricsData?.totalJobs || 0} color="#3b82f6" /></Grid>
                            <Grid size={4}><StatusMetric label="Done" count={metricsData?.jobsDone || 0} color="#10b981" /></Grid>
                            <Grid size={4}><StatusMetric label="Invoiced" count={metricsData?.jobsInvoiced || 0} color="#f59e0b" /></Grid>
                        </Grid>
                    </Panel>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel title="Invoices">
                        <Grid container>
                            <Grid size={4}><StatusMetric label="Paid" count={metricsData?.paidInvoicesCount || 0} color="#10b981" /></Grid>
                            <Grid size={4}><StatusMetric label="Factored" count={metricsData?.factoredCount || 0} color="#3b82f6" /></Grid>
                            <Grid size={4}><StatusMetric label="Non-factored" count={metricsData?.nonFactoredCount || 0} color="#a855f7" /></Grid>
                        </Grid>
                    </Panel>
                </Grid>
            </Grid>

            <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, lg: compact ? 12 : 8 }}>
                    <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FinancialPanel
                                title="All jobs"
                                description="Non-cancelled jobs. Revenue = NTE; cost = tech + parts."
                                financial={metricsData?.financial?.allJobs}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FinancialPanel
                                title="Invoiced jobs"
                                description="Jobs with an invoiced proposal."
                                financial={metricsData?.financial?.invoicedJobs}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12, lg: compact ? 12 : 4 }}>
                    <WorkOrdersPanel workOrders={metricsData?.workOrders} />
                </Grid>
            </Grid>
        </Stack>
    );

    // Access denied view
    if (!isUserReady) {
        return (
            <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (!hasPermission) {
        return (
            <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Card sx={{ maxWidth: 400, p: 4, textAlign: 'center' }}>
                    <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                        Access Denied
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        You do not have permission to view the dashboard. This feature is only available to owners, general managers, accountants, and admins.
                    </Typography>
                </Card>
            </Box>
        );
    }

    // Loading state
    if (metricsLoading) {
        return (
            <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    // Error state
    if (metricsError) {
        return (
            <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh', p: 4 }}>
                <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
                    {metricsError}
                </Alert>
            </Box>
        );
    }

    // Admins and dispatchers are already retrieved from Redux store above

    return (
        <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
            <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {user.first_name} {user.last_name} · {primaryRoleLabel(user) || 'User'}
                    </Typography>
                </Box>

                <Stack spacing={4}>
                    <Box>
                        <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Overview</Typography>
                        <DashboardMetricsSection metricsData={metrics} />
                    </Box>

                    <Box>
                        <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Filtered view</Typography>
                <Card
                    sx={{
                        ...surfaceSx,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setFiltersExpanded((prev) => !prev)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFiltersExpanded((prev) => !prev); } }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1.75,
                            cursor: 'pointer',
                            bgcolor: filtersExpanded ? (isDarkMode ? alpha('#fff', 0.03) : alpha('#000', 0.02)) : 'transparent',
                            borderBottom: filtersExpanded ? '1px solid' : 'none',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                            <FilterListIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <Chip
                                            size="small"
                                            label={activeFilterCount}
                                            sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                        />
                                    )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {filtersApplied ? 'Showing filtered metrics' : 'Client, dates, team, priority'}
                                </Typography>
                            </Box>
                        </Box>
                        <ExpandMoreIcon
                            sx={{
                                transform: filtersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                color: 'text.secondary',
                            }}
                        />
                    </Box>

                    <Collapse in={filtersExpanded}>
                        <Box
                            sx={{
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 1.5, sm: 2 },
                                bgcolor: isDarkMode ? alpha('#fff', 0.02) : alpha('#000', 0.015),
                                width: '100%',
                                minWidth: 0,
                                boxSizing: 'border-box',
                            }}
                        >
                            <Stack spacing={2}>
                                <Stack spacing={1.5}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>
                                            Saved & active
                                        </Typography>
                                        {activeFilterCount > 0 && (
                                            <Chip
                                                size="small"
                                                label={`${activeFilterCount} active`}
                                                sx={{
                                                    height: 22,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.15)' : 'rgba(0,0,0,0.06)',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <DashboardSavedFiltersBar
                                        savedSearches={savedSearchesStorage.searches}
                                        defaultSearchId={savedSearchesStorage.defaultId}
                                        activeSearchId={activeSavedSearchId}
                                        onApplySearch={handleApplySavedSearch}
                                        onSaveSearch={handleSaveSearch}
                                        onDeleteSearch={handleDeleteSavedSearch}
                                        onRenameSearch={handleRenameSavedSearch}
                                        onSetDefault={handleSetDefaultSavedSearch}
                                        onClearDefault={handleClearDefaultSavedSearch}
                                        onResetFilters={handleResetFilters}
                                        isDarkMode={isDarkMode}
                                    />
                                </Stack>

                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        borderColor: isDarkMode ? '#334155' : '#e2e8f0',
                                        bgcolor: 'background.paper',
                                        boxShadow: 'none',
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                                        <Stack spacing={2.5}>
                                            <Box>
                                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                                                    <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                        Client & priority
                                                    </Typography>
                                                </Stack>
                                                <Stack spacing={1.5}>
                                                    <Autocomplete
                                                        size="small"
                                                        fullWidth
                                                        options={clientsData.clients || []}
                                                        value={selectedClient}
                                                        onChange={(_, newValue) => {
                                                            setFilters((prev) => ({
                                                                ...prev,
                                                                clientId: newValue?.id ? String(newValue.id) : '',
                                                            }));
                                                        }}
                                                        getOptionLabel={(option) => option.client_name || ''}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Client"
                                                                placeholder="Search clients..."
                                                                sx={filterInputSx}
                                                            />
                                                        )}
                                                        noOptionsText="No clients found"
                                                    />
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                                                            Priority
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                            {PRIORITY_OPTIONS.map((opt) => (
                                                                <Chip
                                                                    key={opt.value}
                                                                    label={opt.label}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => handlePriorityToggle(opt.value)}
                                                                    sx={priorityChipSx(opt.value)}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Stack>
                                            </Box>

                                            <Divider sx={{ borderColor: isDarkMode ? '#334155' : '#f0f0f0' }} />

                                            <Box>
                                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                                                    <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                        Team
                                                    </Typography>
                                                </Stack>
                                                <Stack spacing={1.5}>
                                                    {canSeeAdminFilter && (
                                                        <Autocomplete
                                                            size="small"
                                                            fullWidth
                                                            options={admins}
                                                            value={selectedAdmin}
                                                            onChange={(_, newValue) => {
                                                                setFilters((prev) => ({
                                                                    ...prev,
                                                                    adminId: newValue?.id ? String(newValue.id) : '',
                                                                }));
                                                            }}
                                                            getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''}`.trim()}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Admin"
                                                                    placeholder="Search admins..."
                                                                    sx={filterInputSx}
                                                                />
                                                            )}
                                                            noOptionsText="No admins found"
                                                        />
                                                    )}
                                                    <Autocomplete
                                                        size="small"
                                                        fullWidth
                                                        options={dispatchers}
                                                        value={selectedDispatcher}
                                                        onChange={(_, newValue) => {
                                                            setFilters((prev) => ({
                                                                ...prev,
                                                                dispatcherId: newValue?.id ? String(newValue.id) : '',
                                                            }));
                                                        }}
                                                        getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''}`.trim()}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Dispatcher"
                                                                placeholder="Search dispatchers..."
                                                                sx={filterInputSx}
                                                            />
                                                        )}
                                                        noOptionsText="No dispatchers found"
                                                    />
                                                </Stack>
                                            </Box>

                                            <Divider sx={{ borderColor: isDarkMode ? '#334155' : '#f0f0f0' }} />

                                            <Box>
                                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                                                    <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                        Job created
                                                    </Typography>
                                                </Stack>
                                                <DateRangePickerDropdown
                                                    label="Created date range"
                                                    placeholder="Any created date"
                                                    startDate={filters.dateFrom}
                                                    endDate={filters.dateTo}
                                                    onStartChange={(date) => {
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            dateFrom: date,
                                                            dateTo: date && prev.dateTo && prev.dateTo < date ? null : prev.dateTo,
                                                        }));
                                                    }}
                                                    onEndChange={(date) => setFilters((prev) => ({ ...prev, dateTo: date }))}
                                                    isDarkMode={isDarkMode}
                                                    sx={{ width: '100%' }}
                                                />
                                            </Box>

                                            <Divider sx={{ borderColor: isDarkMode ? '#334155' : '#f0f0f0' }} />

                                            <Box>
                                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                                                    <ReceiptIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                        Invoice date
                                                    </Typography>
                                                </Stack>
                                                <DateRangePickerDropdown
                                                    label="Invoice date range"
                                                    placeholder="Any invoice date"
                                                    startDate={filters.invoiceDateFrom}
                                                    endDate={filters.invoiceDateTo}
                                                    onStartChange={(date) => {
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            invoiceDateFrom: date,
                                                            invoiceDateTo: date && prev.invoiceDateTo && prev.invoiceDateTo < date ? null : prev.invoiceDateTo,
                                                        }));
                                                    }}
                                                    onEndChange={(date) => setFilters((prev) => ({ ...prev, invoiceDateTo: date }))}
                                                    isDarkMode={isDarkMode}
                                                    sx={{ width: '100%' }}
                                                />
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>

                                <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        disableElevation
                                        onClick={handleApplyFilters}
                                        disabled={filteredMetricsLoading}
                                        sx={{ textTransform: 'none', fontWeight: 600, py: 1 }}
                                    >
                                        {filteredMetricsLoading ? 'Applying…' : 'Apply'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleClearFilters}
                                        disabled={activeFilterCount === 0 && !filtersApplied}
                                        sx={{
                                            textTransform: 'none',
                                            px: 2.5,
                                            whiteSpace: 'nowrap',
                                            borderColor: 'divider',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </Collapse>

                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pt: filtersExpanded ? 1.5 : { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        {filteredMetricsLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                                <CircularProgress size={32} />
                            </Box>
                        )}

                        {filteredMetricsError && (
                            <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                                {filteredMetricsError}
                            </Alert>
                        )}

                        {!filteredMetricsLoading && !filteredMetricsError && !filtersApplied && (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: 4,
                                    px: 2,
                                    borderRadius: 1.5,
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    Set filters above and click Apply to see results.
                                </Typography>
                            </Box>
                        )}

                        {!filteredMetricsLoading && !filteredMetricsError && filtersApplied && filteredMetrics && (
                            <DashboardMetricsSection metricsData={filteredMetrics} compact />
                        )}
                    </CardContent>
                </Card>
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
}
